import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { kind, read, write, subscribe, promptInstall, iosBrowser, isPhone, track } from "./installState";
import "./install.css";

// "Get the app" prompt: real Install button on Chrome / Edge / Samsung, illustrated steps on iPhone / iPad / Mac Safari.
// Never shown inside the installed app, when Chrome says it's already installed, in in-app browsers,
// on a first look, or over the reader, checkout, forms or an open dialog.
const CALM = [/^\/$/, /^\/home\/?$/, /^\/publication\/[^/]+(\/[^/]+)?\/?$/, /^\/account(\/library)?\/?$/, /^\/library\/?$/, /^\/archive\/?$/];
const READER = /^\/(view_paper|view_single_paper)\//;
const SNOOZE_DAYS = [14, 45]; // after the 1st and 2nd "Not now"; after that only from the account menu
const BUSY = ".cd-wrap, .react-responsive-modal-root, .swal-overlay--show-modal, .modal.show, #modal-wrap-cart .react-responsive-modal-overlay";

const ss = (k, v) => { try { if (v === undefined) return sessionStorage.getItem(k); sessionStorage.setItem(k, v); } catch (e) { return null; } return null; };

// Count one visit per browser session.
if (typeof window !== "undefined" && !ss("tns_install_seen")) {
  ss("tns_install_seen", "1");
  write({ visits: (read().visits || 0) + 1 });
}

const ShareGlyph = () => (
  <svg className="ip-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 15V3M8 7l4-4 4 4" /><path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" /></svg>
);
const AddGlyph = () => (
  <svg className="ip-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3.5" y="3.5" width="17" height="17" rx="4" /><path d="M12 8v8M8 12h8" /></svg>
);

export default function InstallPrompt() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("ask"); // ask | steps | done
  const [k, setK] = useState(kind());
  const timer = useRef(null);
  const shownThisSession = useRef(false);

  useEffect(() => subscribe(() => setK(kind())), []);

  // Opened from the account menu ("Get the app").
  useEffect(() => {
    const openNow = () => { if (!kind()) return; setView(kind() === "chrome" ? "ask" : "steps"); setOpen(true); track("menu_open"); };
    window.addEventListener("tns:install-open", openNow);
    return () => window.removeEventListener("tns:install-open", openNow);
  }, []);

  // Reading an edition this session counts as interest.
  useEffect(() => { if (READER.test(pathname)) ss("tns_install_read", "1"); }, [pathname]);

  // Automatic prompt: calm page, interested reader, not snoozed, nothing else on screen.
  useEffect(() => {
    clearTimeout(timer.current);
    if (open) return undefined;
    if (shownThisSession.current || ss("tns_install_shown")) return undefined;
    if (!k || !CALM.some((re) => re.test(pathname))) return undefined;
    const st = read();
    if ((st.dismissed || 0) >= SNOOZE_DAYS.length) return undefined;
    if (st.until && Date.now() < st.until) return undefined;
    if (!ss("tns_install_read") && (st.visits || 0) < 2) return undefined;
    const tryShow = (left) => {
      if (document.querySelector(BUSY) || document.hidden) {
        if (left > 0) timer.current = setTimeout(() => tryShow(left - 1), 5000);
        return;
      }
      shownThisSession.current = true;
      ss("tns_install_shown", "1");
      setView("ask");
      setOpen(true);
      track("shown");
    };
    timer.current = setTimeout(() => tryShow(6), 6000);
    return () => clearTimeout(timer.current);
  }, [pathname, k, open]);

  // Leave the page (e.g. into the reader or checkout): get out of the way.
  useEffect(() => { if (open && !CALM.some((re) => re.test(pathname))) setOpen(false); }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const key = (e) => { if (e.key === "Escape") notNow(); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [open]);

  const phone = isPhone();
  const notNow = () => {
    const n = (read().dismissed || 0) + 1;
    const days = SNOOZE_DAYS[n - 1];
    write({ dismissed: n, until: days ? Date.now() + days * 864e5 : null });
    setOpen(false);
    track("not_now");
  };
  const install = async () => {
    track("install_click");
    const outcome = await promptInstall();
    if (outcome === "accepted") { setView("done"); setTimeout(() => setOpen(false), 4500); }
    else if (outcome === "dismissed") notNow();
    else setOpen(false);
  };


  if (!open) return null;

  const where = phone ? "home screen" : k === "mac" ? "Dock" : "computer";
  const title = view === "done" ? "It’s on your " + where : phone ? "Today’s papers, one tap away" : "Put TheNewspaperStand on your " + where;
  const ios = iosBrowser();

  return (
    <div className={`ip-wrap ${phone ? "ip-sheet" : "ip-card"}`} role="dialog" aria-labelledby="ip-title" aria-describedby="ip-desc">
      <div className="ip-top">
        <img className="ip-icon" src="/apple-touch-icon.png" alt="" width="56" height="56" />
        <div className="ip-head">
          <h2 id="ip-title">{title}</h2>
          <p id="ip-desc">
            {view === "done"
              ? phone ? "Look for the TNS icon. It opens straight to your papers." : "Open it from your apps, Dock or taskbar whenever you like."
              : phone ? "Add TheNewspaperStand to your home screen. It opens full screen, like an app."
              : "Open your papers in their own window, without hunting through browser tabs."}
          </p>
        </div>
        {view !== "done" ? <button type="button" className="ip-x" aria-label="Not now" onClick={notNow}>×</button> : null}
      </div>

      {view === "ask" ? (
        <>
          <ul className="ip-points">
            <li><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M13 2L4 14h7l-1 8 9-12h-7z" /></svg>One tap to today’s papers</li>
            <li><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" /></svg>{phone ? "Full screen, no browser bars" : "Its own window and icon"}</li>
            <li><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>No app store, updates itself</li>
          </ul>
          <div className="ip-actions">
            {k === "chrome"
              ? <button type="button" className="ip-go" onClick={install}>{phone ? "Add to home screen" : "Install app"}</button>
              : <button type="button" className="ip-go" onClick={() => { setView("steps"); track("steps_open"); }}>Show me how</button>}
            <button type="button" className="ip-later" onClick={notNow}>Not now</button>
          </div>
        </>
      ) : null}

      {view === "steps" && k === "ios" ? (
        <>
          <ol className="ip-steps">
            <li><span className="ip-n">1</span><span>Tap <b className="ip-chip"><ShareGlyph />Share</b>{ios === "chrome" ? " at the top right, next to the address bar." : <> in Safari’s toolbar. <small>If you don’t see it, tap <b>•••</b> first.</small></>}</span></li>
            <li><span className="ip-n">2</span><span>Scroll down and tap <b className="ip-chip"><AddGlyph />Add to Home Screen</b></span></li>
            <li><span className="ip-n">3</span><span>Tap <b>Add</b>. The TNS icon appears on your home screen.</span></li>
          </ol>
          <div className="ip-mock" aria-hidden="true">
            <div className="ip-mock-row"><span>Add to Home Screen</span><AddGlyph /></div>
          </div>
          <div className="ip-actions"><button type="button" className="ip-later ip-wide" onClick={() => { setOpen(false); track("steps_done"); }}>Got it</button></div>
        </>
      ) : null}

      {view === "steps" && k === "mac" ? (
        <>
          <ol className="ip-steps">
            <li><span className="ip-n">1</span><span>In the menu bar at the top of your screen, click <b>File</b>.</span></li>
            <li><span className="ip-n">2</span><span>Choose <b className="ip-chip">Add to Dock…</b> then click <b>Add</b>.</span></li>
          </ol>
          <div className="ip-actions"><button type="button" className="ip-later ip-wide" onClick={() => { setOpen(false); track("steps_done"); }}>Got it</button></div>
        </>
      ) : null}
    </div>
  );
}

// For the account menu: what this device can do ("chrome" | "ios" | "mac" | null), kept up to date.
export const useInstallKind = () => {
  const [v, setV] = useState(kind());
  useEffect(() => subscribe(() => setV(kind())), []);
  return v;
};
